import * as React from "react";
import {Alert, Button, Modal} from "react-bootstrap/lib";
import {FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ErrorDisplay} from "../common/ui/errordisplay/ErrorDisplay";
import {Spinner} from "../common/ui/spinner/Spinner";
import {mapErrorToMessage} from "../common/util/ErrorMapper";
import {WithApi, WithApiProperties} from "../common/util/WithApi";
import {AccessRule} from "../model";
import {SecurityForm} from "../security/SecurityForm";
import {Role} from "../user/model";
import {RESTRICT_NAVIGATION_BOUNDS} from "../common/ui/riamap/Previewer";
import {RIAMap} from "../common/ui/riamap/RIAMap";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {getPolygonPointsFromFeature} from "../security/model";

export interface DataSecurityDialogProps {
  onSuccess?: () => void;
  onHide?: () => void;
  dataId: string;
  dataType: string;
  onUpdateDefaultRoles?: (defaultRoles: AccessRule[]) => void;
}

interface DataSecurityDialogState {
  isLoading: boolean;
  isExecuting: boolean;
  isSuccess: boolean;
  error: Error;
  accessRules: AccessRule[];
  isUserManagementActive: boolean;
  allRoles: Role[];
  dataType: string;
  unmappedRoles: AccessRule[];
  mapDrawModeActive: boolean;
  mapEditModeActive: boolean;
  spatialAreaCoordinates: number[];
  prevSpatialAreaCoordinates: number[];
}

class DataSecurityDialogComponent extends React.Component<DataSecurityDialogProps & InjectedIntlProps & WithApiProperties, DataSecurityDialogState> {
  private securityForm: any;
  private mapInit: boolean;

  constructor(props) {
    super(props);
    this.mapInit = false;
    this.state = {
      isLoading: false,
      isExecuting: false,
      isSuccess: false,
      error: null,
      accessRules: [],
      isUserManagementActive: null,
      allRoles: [],
      dataType: this.props.dataType,
      unmappedRoles: [],
      mapDrawModeActive: false,
      mapEditModeActive: false,
      spatialAreaCoordinates: null,
      prevSpatialAreaCoordinates: null,
    };
  }

  componentDidMount() {
    if (this.props.dataType === "none") {
      this.loadDefaults();
    } else {
      this.load();
    }
  }

  private load() {
    const {api, dataId} = this.props;

    this.setState({isLoading: true});
    Promise.all([
      api.getCrawlAccessRules(),
      api.getAccessRules(dataId),
      api.isUserManagementActive(),
      api.getAllRoles(),
    ])
        .then(([
                 unmappedRoles,
                 accessRules,
                 isUserManagementActive,
                 allRoles,
               ]) => {
          this.setState({
            isLoading: false,
            accessRules,
            isUserManagementActive,
            allRoles,
            unmappedRoles,
          });
        });
  }

  private loadDefaults() {
    const {api} = this.props;

    this.setState({isLoading: true});
    Promise.all([
      api.getAllRolesWithDefaultAccessRules(),
      api.getCrawlAccessRules(),
      api.isUserManagementActive(),
      api.getAllRoles(),
    ])
        .then(([
                 unmappedRoles,
                 accessRules,
                 isUserManagementActive,
                 allRoles,
               ]) => {
          this.setState({
            isLoading: false,
            unmappedRoles,
            accessRules,
            isUserManagementActive,
            allRoles,
          });
        });
  }

  closeDialog = () => {
    this.props.onHide();
  }

  setSecurityFormModalRef = ref => {
    if (ref) {
      this.securityForm = ref.getWrappedInstance();
    }
  }

  saveChanges = () => {
    const {dataId, dataType} = this.props;
    this.setState({isLoading: false, isExecuting: true, error: null, isSuccess: false});
    const launchedFromSettingsPage: boolean = dataType === "none";

    const result = this.securityForm.getResult();

    if (launchedFromSettingsPage) {
      if (result.deletedPermissionsToSave?.length > 0) {
        this.props.onUpdateDefaultRoles(result.deletedPermissionsToSave);
      }
      if (result.permissionsToSave?.length > 0) {
        this.props.onUpdateDefaultRoles(result.permissionsToSave);
      }
      this.setState({isExecuting: false, error: null, isSuccess: true});
      this.closeDialog();
      this.props.onSuccess?.();
    } else {
      Promise.resolve()
          .then(() => {
            if (result.deletedPermissionsToSave?.length > 0) {
              return this.props.api.deleteAccessRules(dataId, result.deletedPermissionsToSave);
            }
          })
          .then(() => {
            if (result.permissionsToSave?.length > 0) {
              return this.props.api.setAccessRules(dataId, result.permissionsToSave);
            }
          })
          .then(() => {
            this.setState({isExecuting: false, error: null, isSuccess: true});
            this.closeDialog();
            this.props.onSuccess?.();
          })
          .catch((error) => {
            this.setState({isExecuting: false, error, isSuccess: false});
          });
    }
  }

  private onDrawSpatialArea = () => {
    this.mapInit = true;
    this.setState(prevState => ({
      mapDrawModeActive: true,
      spatialAreaCoordinates: [],
      prevSpatialAreaCoordinates: prevState.spatialAreaCoordinates
    }));
  }

  private onEditSpatialArea = (coordinates: number[]) => {
    if (!this.mapInit) {
      this.mapInit = true;
      this.setState({mapEditModeActive: true, spatialAreaCoordinates: coordinates});
      //This starts the edit map again if it is the first time launching.
      // If it is the first time starting the map closes when the initial polygon is drawn.
      setTimeout(() => {
        this.onEditSpatialArea(coordinates)
      }, 1000);
    } else {
      this.setState(prevState => ({
        mapEditModeActive: true,
        prevSpatialAreaCoordinates: prevState.spatialAreaCoordinates,
        spatialAreaCoordinates: coordinates
      }));
    }
  }

  private onDrawCancel = () => {
    this.setState(prevState => ({
      mapDrawModeActive: false,
      spatialAreaCoordinates: prevState.prevSpatialAreaCoordinates
    }));
  }

  private onEditCancel = () => {
    this.setState(prevState => ({
      mapEditModeActive: false,
      spatialAreaCoordinates: prevState.prevSpatialAreaCoordinates
    }));
  }

  private onFeatureDrawn = (feature: Feature): boolean => {
    this.setState({
      spatialAreaCoordinates: getPolygonPointsFromFeature(feature),
      mapDrawModeActive: false,
      mapEditModeActive: false,
    });
    return true;
  }

  renderTitle() {
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }
    return <Modal.Title><FormattedMessage id="studio.data-security-dialog.title"
                                          defaultMessage="Security"/></Modal.Title>;
  }

  renderButtons() {
    const {isExecuting, isSuccess} = this.state;

    if (isExecuting || isSuccess) {
      return null;
    }

    return (
        <div>
          <Button onClick={this.saveChanges}>
            <FormattedMessage id="studio.data-security-dialog.ok-button" defaultMessage="OK"/>
          </Button>
          <Button data-cy="cancel" onClick={this.closeDialog}><FormattedMessage
              id="studio.ui.form-buttons.cancel"
              defaultMessage="Cancel"/></Button>
        </div>
    );
  }

  renderMessage(message: React.ReactElement) {
    return (
        <div style={{textAlign: "center"}}>
          <h2>{message}</h2>
          <Spinner bigStyle/>
        </div>
    );
  }

  renderExecuting() {
    return this.renderMessage(<FormattedMessage id="studio.data-security-dialog.submitting"
                                                defaultMessage="Submitting changes..."/>);
  }

  renderLoading() {
    return this.renderMessage(<FormattedMessage id="studio.data-security-dialog.loading" defaultMessage="Loading..."/>);
  }

  renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error);
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  renderBody() {
    const {isLoading, isExecuting, dataType} = this.state;
    const {api} = this.props;
    const background = api.getPreviewBackground();

    // If dataType is "none", this was called from Settings-> Crawling Defaults page.
    const addDefaultRoles = dataType === "none";

    if (isLoading) {
      return this.renderLoading();
    }

    if (isExecuting) {
      return this.renderExecuting();
    }

    return (
        <div>
          {this.renderError()}
          <div style={{
            display: (!this.state.mapDrawModeActive && !this.state.mapEditModeActive) ? "block" : "none",
          }}>
            <SecurityForm
                key={`security-form-${isLoading}`}
                ref={this.setSecurityFormModalRef}
                accessRules={this.state.accessRules}
                isUserManagementActive={this.state.isUserManagementActive}
                allRoles={this.state.allRoles}
                dataType={this.props.dataType}
                onDrawSpatialArea={this.onDrawSpatialArea}
                onEditSpatialArea={this.onEditSpatialArea}
                spatialAreaCoordinates={this.state.spatialAreaCoordinates}
                addCrawlDefaultRoles={addDefaultRoles}
                unmappedRoles={this.state.unmappedRoles}
            />
          </div>
          <RIAMap style={{
            width: "100%", height: "400px",
            display: (this.state.mapDrawModeActive || this.state.mapEditModeActive) ? "block" : "none",
          }}
                  background={background}
                  dataToVisualize={[]}
                  shouldFit={true}
                  shouldAnimateFit={true}
                  restrictNavigationBounds={background.bounds ? background.bounds : RESTRICT_NAVIGATION_BOUNDS}
                  shouldHideLayerTree={true}
                  shouldAddGridLayer={false}
                  showPreview={true}
                  drawModeActive={this.state.mapDrawModeActive}
                  editModeActive={this.state.mapEditModeActive}
                  onDrawCancel={this.onDrawCancel}
                  onEditCancel={this.onEditCancel}
                  editFeatureCoordinates={this.state.spatialAreaCoordinates}
                  onFeatureDrawn={this.onFeatureDrawn}
          />
        </div>
    );
  }

  render() {

    const {isExecuting} = this.state;
    return (
        <Modal data-cy="data-security-dialog" show={true} style={{"visibility": "visible"}}
               onHide={this.closeDialog} backdrop={"static"}>
          <Modal.Header closeButton={!isExecuting}>
            {this.renderTitle()}
          </Modal.Header>
          <Modal.Body id={"add-modal-body"}>
            {this.renderBody()}
          </Modal.Body>
          <Modal.Footer>
            {this.renderButtons()}
          </Modal.Footer>
        </Modal>
    );
  }
}

export const DataSecurityDialog = WithApi(injectIntl(DataSecurityDialogComponent));